import { useState } from "react";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Spinner } from "@/components/ui/spinner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  useCreateKitchenCapacityWindow,
  useDeleteKitchenCapacityWindow,
  useKitchenCapacityWindows,
  useUpdateKitchenCapacityWindow,
} from "@/hooks/use-kitchen-capacity";
import type { ApiKitchenCapacityWindow } from "@/types/api";

// 0=Mon … 6=Sun in the backend; we display in that order.
const DAYS = [
  { value: 0, label: "Lunes" },
  { value: 1, label: "Martes" },
  { value: 2, label: "Miércoles" },
  { value: 3, label: "Jueves" },
  { value: 4, label: "Viernes" },
  { value: 5, label: "Sábado" },
  { value: 6, label: "Domingo" },
] as const;

const DAY_LABEL = new Map(DAYS.map((d) => [d.value, d.label]));

function toTimeInput(value: string): string {
  return value.slice(0, 5);
}

interface DraftState {
  id: string | null;
  day_of_week: number;
  start_time: string;
  end_time: string;
  max_pax: number;
}

function emptyDraft(): DraftState {
  return { id: null, day_of_week: 2, start_time: "19:00", end_time: "22:30", max_pax: 20 };
}

export function KitchenCapacityPanel() {
  const { data: windows, isLoading } = useKitchenCapacityWindows();
  const createWindow = useCreateKitchenCapacityWindow();
  const updateWindow = useUpdateKitchenCapacityWindow();
  const deleteWindow = useDeleteKitchenCapacityWindow();

  const [draft, setDraft] = useState<DraftState | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<ApiKitchenCapacityWindow | null>(null);

  const sortedWindows = (windows ?? []).slice().toSorted((a, b) => {
    if (a.day_of_week !== b.day_of_week) return a.day_of_week - b.day_of_week;
    return a.start_time.localeCompare(b.start_time);
  });

  const startCreate = () => setDraft(emptyDraft());
  const startEdit = (w: ApiKitchenCapacityWindow) =>
    setDraft({
      id: w.id,
      day_of_week: w.day_of_week,
      start_time: toTimeInput(w.start_time),
      end_time: toTimeInput(w.end_time),
      max_pax: w.max_pax,
    });
  const closeDraft = () => setDraft(null);

  const submitDraft = () => {
    if (!draft) return;
    if (draft.start_time >= draft.end_time) return;
    if (draft.max_pax < 1) return;
    const payload = {
      day_of_week: draft.day_of_week,
      start_time: draft.start_time,
      end_time: draft.end_time,
      max_pax: draft.max_pax,
    };
    if (draft.id) {
      updateWindow.mutate({ id: draft.id, changes: payload }, { onSuccess: closeDraft });
    } else {
      createWindow.mutate(payload, { onSuccess: closeDraft });
    }
  };

  const isSubmitting = createWindow.isPending || updateWindow.isPending;
  const draftValid = draft != null && draft.start_time < draft.end_time && draft.max_pax >= 1;

  return (
    <section className="space-y-4">
      <header className="space-y-1">
        <h2 className="text-lg font-semibold">Capacidad de cocina por franja</h2>
        <p className="text-sm text-muted-foreground">
          Define cuánta gente puede reservar simultáneamente para proteger la cocina. Sin franjas
          configuradas, el límite es solo la disponibilidad de mesas. Walk-ins no cuentan en este
          tope; el staff puede sobrepasarlo confirmando manualmente.
        </p>
      </header>

      {isLoading ? (
        <div className="flex h-32 items-center justify-center">
          <Spinner />
        </div>
      ) : sortedWindows.length === 0 ? (
        <div className="rounded-lg border border-dashed p-6 text-center text-sm text-muted-foreground">
          Sin franjas configuradas. La cocina acepta cualquier cantidad mientras haya mesas.
        </div>
      ) : (
        <div className="overflow-hidden rounded-lg border">
          <table className="w-full text-sm">
            <thead className="bg-muted/50">
              <tr className="text-left">
                <th className="px-4 py-2 font-medium">Día</th>
                <th className="px-4 py-2 font-medium">Desde</th>
                <th className="px-4 py-2 font-medium">Hasta</th>
                <th className="px-4 py-2 font-medium">Máx pax</th>
                <th className="px-4 py-2" />
              </tr>
            </thead>
            <tbody>
              {sortedWindows.map((w) => (
                <tr key={w.id} className="border-t">
                  <td className="px-4 py-2">{DAY_LABEL.get(w.day_of_week) ?? w.day_of_week}</td>
                  <td className="px-4 py-2 tabular-nums">{toTimeInput(w.start_time)}</td>
                  <td className="px-4 py-2 tabular-nums">{toTimeInput(w.end_time)}</td>
                  <td className="px-4 py-2 tabular-nums">{w.max_pax}</td>
                  <td className="px-4 py-2 text-right">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => startEdit(w)}
                      aria-label="Editar"
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setConfirmDelete(w)}
                      aria-label="Eliminar"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div>
        <Button onClick={startCreate} variant="outline" size="sm">
          <Plus className="mr-1 h-4 w-4" /> Agregar franja
        </Button>
      </div>

      <Dialog open={draft !== null} onOpenChange={(open) => (open ? null : closeDraft())}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{draft?.id ? "Editar franja" : "Agregar franja"}</DialogTitle>
            <DialogDescription>
              Cap por la suma de pax de reservas que se solapan en cada slot de 15 minutos.
            </DialogDescription>
          </DialogHeader>
          {draft != null && (
            <div className="grid gap-4">
              <div className="grid gap-2">
                <Label htmlFor="kitchen-cap-day">Día</Label>
                <select
                  id="kitchen-cap-day"
                  className="h-9 rounded-md border bg-background px-3 text-sm"
                  value={draft.day_of_week}
                  onChange={(e) => setDraft({ ...draft, day_of_week: Number(e.target.value) })}
                >
                  {DAYS.map((d) => (
                    <option key={d.value} value={d.value}>
                      {d.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="grid gap-2">
                  <Label htmlFor="kitchen-cap-start">Desde</Label>
                  <Input
                    id="kitchen-cap-start"
                    type="time"
                    value={draft.start_time}
                    onChange={(e) => setDraft({ ...draft, start_time: e.target.value })}
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="kitchen-cap-end">Hasta</Label>
                  <Input
                    id="kitchen-cap-end"
                    type="time"
                    value={draft.end_time}
                    onChange={(e) => setDraft({ ...draft, end_time: e.target.value })}
                  />
                </div>
              </div>
              <div className="grid gap-2">
                <Label htmlFor="kitchen-cap-max">Máx pax simultáneo</Label>
                <Input
                  id="kitchen-cap-max"
                  type="number"
                  min={1}
                  value={draft.max_pax}
                  onChange={(e) => setDraft({ ...draft, max_pax: Number(e.target.value) })}
                />
              </div>
              {!draftValid && (
                <p className="text-xs text-destructive">
                  La hora de cierre debe ser posterior al inicio y el máx pax debe ser ≥ 1.
                </p>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={closeDraft} disabled={isSubmitting}>
              Cancelar
            </Button>
            <Button onClick={submitDraft} disabled={!draftValid || isSubmitting}>
              {draft?.id ? "Guardar" : "Crear"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={confirmDelete !== null}
        onOpenChange={(open) => (open ? null : setConfirmDelete(null))}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar franja?</AlertDialogTitle>
            <AlertDialogDescription>
              {confirmDelete && (
                <>
                  Vas a quitar la franja de {DAY_LABEL.get(confirmDelete.day_of_week)}{" "}
                  {toTimeInput(confirmDelete.start_time)}–{toTimeInput(confirmDelete.end_time)} (máx{" "}
                  {confirmDelete.max_pax}). Las reservas existentes no se tocan; sólo desaparece el
                  tope para futuras creaciones.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (!confirmDelete) return;
                deleteWindow.mutate(confirmDelete.id, { onSuccess: () => setConfirmDelete(null) });
              }}
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}
